import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bootstrap, PlatformProvider, usePlatform, type PlatformState } from '@cyntox/common/platform';
import { APP_LABELS, type AppKey } from '@cyntox/common/AppShell';
import { api } from '../api/client';
import { getAuthConfig } from '../api/auth';
import type { FleetReference } from '../api/types';

/* ---------------------------------------------------------------------------
   The signed-in person and their access come from the shared platform. This
   adds what Fleet's screens need: the classes and their fields, origins,
   statuses, ownership kinds, the routes a unit can run, and Admin's regions and places.
   --------------------------------------------------------------------------- */

interface AppState extends PlatformState {
  reference: FleetReference;
  statusLabel: (code: string) => string;
  ownershipLabel: (code?: string | null) => string;
  originName: (code?: string | null) => string;
}

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp must be used inside <AppProvider>');
  return v;
}

export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <PlatformProvider appLabel="Fleet">
      <FleetProvider>{children}</FleetProvider>
    </PlatformProvider>
  );
}

function FleetProvider({ children }: { children: ReactNode }) {
  const platform = usePlatform();
  const hasAccess = platform.can('vehicles', 'view');
  const refQuery = useQuery({
    queryKey: ['reference'],
    queryFn: () => api.get<FleetReference>('/api/reference'),
    enabled: hasAccess,
  });

  const value = useMemo<AppState | null>(() => {
    const reference = refQuery.data;
    if (!reference) return null;
    return {
      ...platform,
      reference,
      statusLabel: (code) => reference.statuses.find((s) => s.code === code)?.label ?? code,
      ownershipLabel: (code) => (code ? reference.ownerships.find((o) => o.code === code)?.label ?? code : '—'),
      originName: (code) => (code ? reference.origins.find((o) => o.originCode === code)?.name ?? code : '—'),
    };
  }, [platform, refQuery.data]);

  if (!hasAccess) return <NothingHere />;

  if (refQuery.isError) {
    return (
      <Bootstrap title="Could not load Fleet">
        <p>{(refQuery.error as Error).message}</p>
      </Bootstrap>
    );
  }
  if (!value) return <Bootstrap title="Loading Fleet…">{null}</Bootstrap>;

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function NothingHere() {
  const apps = getAuthConfig()?.apps ?? {};
  const others = (Object.keys(APP_LABELS) as AppKey[]).filter((key) => key !== 'fleet' && apps[key]);
  return (
    <Bootstrap title="Nothing in Fleet for your access">
      <p>Your role doesn't include Fleet. An administrator gives Fleet access in Admin → Access.</p>
      {others.length > 0 && (
        <p>
          {others.map((key, i) => (
            <span key={key}>{i > 0 && ' · '}<a href={apps[key]}>{APP_LABELS[key]}</a></span>
          ))}
        </p>
      )}
    </Bootstrap>
  );
}
